import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field'
import { type NewTeammate, shareApplication } from '@/models/confirmInfo'
import { fillApplicationErrorMessage } from '@/models/fillApplication'
import { isPersonFilled, NewPersonFields, NO_PERSON } from './NewPersonFields'

interface ShareApplicationDialogProps {
  orgId: string
  orgName: string
  onOpenChange: (open: boolean) => void
}

/** Mounted only while open, so every opening starts blank. */
export function ShareApplicationDialog({ orgId, orgName, onOpenChange }: ShareApplicationDialogProps) {
  const [teammate, setTeammate] = useState<NewTeammate>(NO_PERSON)
  const share = useMutation({
    mutationFn: () =>
      shareApplication(orgId, {
        firstName: teammate.firstName.trim(),
        lastName: teammate.lastName.trim(),
        email: teammate.email.trim(),
      }),
    onSuccess: (shared) => {
      toast.success(`We emailed ${shared.email} a link to your application`)
      onOpenChange(false)
    },
  })

  function submit(event: React.FormEvent) {
    event.preventDefault()
    if (isPersonFilled(teammate)) share.mutate()
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-6">
          <DialogHeader>
            <DialogTitle>Share your application</DialogTitle>
            <DialogDescription>
              Invite someone at {orgName} to check the application or fill in what&apos;s missing. We&apos;ll
              email them a link that signs them in.
            </DialogDescription>
          </DialogHeader>
          <NewPersonFields idPrefix="share" person={teammate} onChange={setTeammate} />
          {share.isError && <FieldError>{fillApplicationErrorMessage(share.error)}</FieldError>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isPersonFilled(teammate)}
              loading={share.isPending ? 'Sending…' : undefined}
            >
              Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
